import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router';

// Root-relative asset paths need BASE_PATH prepended, the same as project images.
const withBasePath = (src) => (
  src && src.startsWith('/')
    ? `${BASE_PATH}${src}`.replace(/([^:]\/)\/+/g, '$1')
    : src
);

// Route internal links through react-router; leave mailto/external links alone.
const components = {
  a: ({ href, children }) => (
    href && href.startsWith('/')
      ? <Link to={href}>{children}</Link>
      : <a href={href}>{children}</a>
  ),
  img: ({ src, alt, title }) => (
    <figure className="looseleaf-figure">
      <img src={withBasePath(src)} alt={alt || ''} />
      {title ? <figcaption>{title}</figcaption> : null}
    </figure>
  ),
  // Markdown puts a standalone image inside a paragraph, but <figure> and
  // <figcaption> are not valid descendants of <p>. Drop the wrapper in that case.
  p: ({ children, node }) => {
    const kids = node && node.children ? node.children : [];
    const isLoneImage = kids.length === 1 && kids[0].tagName === 'img';
    const isIframe = kids.length === 1 && kids[0].tagName === 'iframe';
    return (isLoneImage || isIframe) ? children : <p>{children}</p>;
  },
  iframe: ({ src, title, width, height, style }) => (
    <iframe
      src={src}
      title={title}
      width={width || "100%"}
      height={height || "500px"}
      style={{ border: 'none', borderRadius: '8px', overflow: 'hidden', ...style }}
    />
  )
};

// react-markdown is CommonMark only, so tables and strikethrough need
// remark-gfm. rehype-raw keeps inline HTML working, as on the About page.
const Markdown = ({ children }) => (
  <ReactMarkdown
    components={components}
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeRaw]}
  >
    {children}
  </ReactMarkdown>
);

Markdown.propTypes = {
  children: PropTypes.string.isRequired,
};

export default Markdown;
